/**
 * Canonical profile-field keys used by the profile atoms. These keys are stable
 * atom API, while the values in {@link ProfileFieldMap} are tenant-specific SSO
 * profile segment field names.
 */
export type ProfileFieldKey =
  // Personal info
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'birthDate'
  | 'email'
  | 'gender'
  | 'title'
  | 'imageUrl'
  | 'playerNumber'
  // Address
  | 'addressStreet1'
  | 'addressStreet2'
  | 'addressCity'
  | 'addressRegion'
  | 'addressPostalCode'
  | 'addressCountry'
  // Phone
  | 'phoneMobile'
  | 'phoneLandline'
  // Supporter / loyalty (read-only display)
  | 'supporterSchemeLevelNames'
  | 'supporterTicketingMemberId'
  | 'supporterAccountBalance';

// Marketing opt-in fields are intentionally NOT in the canonical key set.
// Each tenant declares its own marketing taxonomy on the atom instance via
// the `marketingSections` schema prop, where each entry references its SSO
// field name directly. This keeps tenant-specific email-list categories out
// of the shared atom contract.

export type ProfileFieldMap = Record<ProfileFieldKey, string>;

/**
 * Default canonical-to-SSO-name mapping. Matches the SportsAlliance profile
 * segment shape used by every currently-shipping FA tenant.
 */
export const DEFAULT_PROFILE_FIELDS: ProfileFieldMap = {
  // Personal info
  firstName: 'first_name',
  middleName: 'middle_name',
  lastName: 'last_name',
  birthDate: 'birth_date',
  email: 'email',
  gender: 'gender',
  title: 'title',
  imageUrl: 'image_url',
  playerNumber: 'player_number',
  // Address
  addressStreet1: 'address_street_1',
  addressStreet2: 'address_street_2',
  addressCity: 'address_city',
  addressRegion: 'address_division_1',
  addressPostalCode: 'address_postal',
  addressCountry: 'address_country',
  // Phone
  phoneMobile: 'phone_mobile',
  phoneLandline: 'phone',
  // Supporter / loyalty
  supporterSchemeLevelNames: 'supporter_scheme_level_names',
  supporterTicketingMemberId: 'supporter_ticketing_member_id',
  supporterAccountBalance: 'supporter_account_balance',
};
